import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from './theme';

// Components that map cleanly to dz-shared-styles BEM classes (button,
// dropdown, tab-buttons, tile) are plain React wrappers below. Components
// without a shared equivalent stay as Emotion styled blocks but read all
// their values from theme.ts which itself resolves to var(--…) tokens.

const cn = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

// ─── Shared-class wrappers ────────────────────────────────────────────────

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Card({ className, style, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('tile', 'tile--elevation-low', className)}
        // Padding is overridable via --dz-card-pad (the widget root sets a
        // smaller value on mobile) and falls back to the lg default elsewhere.
        style={{ padding: `var(--dz-card-pad, ${theme.spacing.lg})`, ...style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

// Bare section wrapper for groupings that should sit flush on the page
// background — no tile chrome, no inner padding. Mirrors the same helper
// in widgets/budget; the table area in particular uses this instead of
// `Card`. Bottom margin keeps vertical rhythm against neighbouring
// sections. forwardRef preserves parity with `Card`.
export const Section = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Section({ className, style, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={className}
        style={{ marginBottom: theme.spacing.lg, ...style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 's' | 'm';
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ variant = 'secondary', size = 'm', className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={cn(
          'button',
          `button--${variant}`,
          `button--size-${size}`,
          className,
        )}
        {...rest}
      />
    );
  },
);

export const ToggleGroup = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function ToggleGroup({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('tab-buttons', 'tab-buttons--secondary', 'tab-buttons--size-s', className)}
        {...rest}
      />
    );
  },
);

interface ToggleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}
export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  function ToggleButton({ active, className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={cn(
          'tab-buttons__button',
          active && 'tab-buttons__button--active',
          className,
        )}
        {...rest}
      />
    );
  },
);

// ─── Emotion-only primitives (no shared-styles equivalent) ────────────────

// Matches dz-shared-styles `@mixin serif-l`: 1.875rem GT-Alpina with -0.02em
// tracking. Used for table/section headlines (e.g. "Investitionsprojekte").
// Reproduces the mixin literally because shared exposes it as Sass only,
// not as a CSS class — the widget can't @include it from CSS-in-JS.
//
// An h2, not an h3: the host page owns the h1 and the widget's sections are its
// top-level regions. It emitted h3/h4 only, which left the document outline
// skipping a level and ranked these captions equal with the host footer's
// blocks. The `&&` wrapper is what makes that safe — it doubles the Emotion
// class (specificity 0,2,0) so a host theme rule like `.entry-content h2 {…}`
// (0,1,1), which could not match while we emitted no h2, cannot now override
// the type scale. Same reasoning on every heading the widget renders.
export const CardTitle = styled.h2`
  && {
    /* dz-shared-styles sets text-align: center on bare h2, and the host theme
       adds horizontal padding. The && wrapper only beats them for properties
       declared here, so both have to be declared. */
    text-align: left;
    padding: 0;
    font-family: var(--serif-stack);
    font-size: ${scaled(30)};
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-weight: normal;
    color: ${theme.colors.text.primary};
    margin: 0 0 ${theme.spacing.xs} 0;
  }
`;

// Chart-card title (`Industrien`, `Herkunftsländer`). Matches the DZ
// Infografiken bar-chart template, where the chart title is the big
// GT-Alpina(M) serif — 30 px / 1.875rem, 400, -2% tracking — the same
// face used for section headers like `Investitionsprojekte`.
export const ChartCardTitle = styled.h2`
  && {
    /* dz-shared-styles sets text-align: center on bare h2, and the host theme
       adds horizontal padding. The && wrapper only beats them for properties
       declared here, so both have to be declared. */
    text-align: left;
    padding: 0;
    font-family: var(--serif-stack);
    font-size: ${scaled(30)};
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-weight: 400;
    color: var(--color-text-default);
    margin: 0;
  }
`;

// Sans-s body text for the small subtitle line under a CardTitle (e.g.
// "8 Einträge").
export const CardSubtitle = styled.p`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.2;
  color: ${theme.colors.text.muted};
  margin: 0;
`;

export const FlexRow = styled.div<{ gap?: string; wrap?: boolean; align?: string; justify?: string }>`
  display: flex;
  flex-direction: row;
  gap: ${props => props.gap || theme.spacing.md};
  flex-wrap: ${props => props.wrap ? 'wrap' : 'nowrap'};
  align-items: ${props => props.align || 'center'};
  justify-content: ${props => props.justify || 'flex-start'};
`;

export const FlexCol = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.gap || theme.spacing.md};
`;

export const Chip = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.xl};
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  background: ${props => props.active ? 'var(--primary-color)' : theme.colors.background};
  color: ${props => props.active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover { background: ${props => props.active ? 'var(--color-data-700)' : 'var(--color-bg-muted)'}; }
`;

export const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

export const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const ErrorMessage = styled.div`
  padding: ${theme.spacing.md};
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.error};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
`;
